import Dialog from './Dialog';
import common from '../common';
import Text from './Text';
import i18next from 'i18next';

export default class OverviewPanel extends Dialog {
	constructor() {
		super(850, 450, null);

		this._buttons = null;
		this._activeButton = null;
		this._activeButtonState = null;
		this.timerClock = null;
		this.timerEnd = null;
		this.timeStart = null;
		this._picture = null;
		this._fade = null;
		this._achievementIndex = 0;

		this._objectives = {
			housing: null,
			school: null,
			hospital: null,
			mission: null,
		};

		this.signals = {};
		this.signals.opened = new P3.utils.MiniSignal();

		this._strings = { // We need one of these for every scenario. And gotta rework it to change with difficulty.
			SCHOOL: common.scenario.targets.school > 1 ? i18next.t('gui.general.schools') : i18next.t('structure.school.description'),
			HOSPITAL: common.scenario.targets.hospital > 1 ? i18next.t('gui.general.hospitals') : i18next.t('structure.hospital.description'),
		};

		this._languageChangeSignal = common.languageManager.signals.changed.add(this.onLanguageChange, this);

		this.init();
	}

	init() {
		this._fade = new PIXI.Graphics();
		this._fade.beginFill(0x0, 0.5);
		this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
		this._fade.endFill();
		this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, this._fade.height * 0.5);
		this.addChildAt(this._fade, 0);

		this._buttons = [];

		const t = i18next.t('gui.overviewpanel.title', { SCENARIO: i18next.t(`gui.selectscenario.scenario${common.selectedScenario}.title`) });

		this.setTitleText(t);

		this._panel.body.overview = this._createObjectivesBody();
		this._panel.body.overview.position.set(50, 0);
		this._panel.body.addChild(this._panel.body.overview);

		this._panel.body.height	= this._panel.body.overview.height + 150;

		this.checkObjectives();
	}

	checkObjectives() {
		const targets = common.scenario.targets;
		const progress = common.scenario.targetProgress;

		for (const i in targets) {
			if (progress[i] >= targets[i]) {
				this.toggleObjective(i, true);
			} else {
				this.toggleObjective(i, false);
			}
		}
	}

	onCloseButtonClick() {
		this.visible = false;
	}

	onLanguageChange() {
		this._strings.SCHOOL = common.scenario.targets.school > 1 ? i18next.t('gui.general.schools') : i18next.t('structure.school.description');
		this._strings.HOSPITAL = common.scenario.targets.hospital > 1 ? i18next.t('gui.general.hospitals') : i18next.t('structure.hospital.description');

		this._objectives.housing.value.text = i18next.t('gui.overviewpanel.goals.housing.value', { ...this._strings, NUMBER: common.scenario.targets.housing });
		this._objectives.school.value.text = i18next.t('gui.overviewpanel.goals.school.value', { ...this._strings, NUMBER: common.scenario.targets.school });
		this._objectives.hospital.value.text = i18next.t('gui.overviewpanel.goals.hospital.value', { ...this._strings, NUMBER: common.scenario.targets.hospital });
		this._objectives.mission.value.text = i18next.t('gui.overviewpanel.goals.hotel.value', { ...this._strings, NUMBER: common.scenario.targets.mission });
	}

	_createObjectivesBody() {
		const section = new PIXI.Container();

		const housing = this._createObjective('housing');

		housing.position.set(0, 100);
		this._objectives.housing = housing;
		section.addChild(housing);

		const school = this._createObjective('school');

		school.position.set(0, housing.y + housing.height);
		this._objectives.school = school;
		section.addChild(school);

		const hospital = this._createObjective('hospital');

		hospital.position.set(0, school.y + school.height);
		this._objectives.hospital = hospital;
		section.addChild(hospital);

		const mission = this._createObjective('hotel');

		mission.position.set(0, hospital.y + hospital.height);
		this._objectives.mission = mission;
		section.addChild(mission);

		const limits = new PIXI.Container();

		limits.position.set(520, 130);
		section.addChild(limits);

		limits.budget = new Text('gui.overviewpanel.maxbudget', { font: '30px', align: 'left', tint: 0x0, maxWidth: 220 });
		limits.budget.anchor.set(0, 0);
		limits.budget.position.set(0, 0);
		limits.addChild(limits.budget);

		limits.budget.value = new Text(`$${common.numberWithCommas(common.scenario.budget.total)}`, { font: '30px', align: 'left', tint: 0x0, maxWidth: 180 });
		limits.budget.value.anchor.set(1, 0);
		limits.budget.value.position.set(320, 0);
		limits.budget.addChild(limits.budget.value);

		limits.damage = new Text('gui.overviewpanel.maxdamage', { font: '30px', align: 'left', tint: 0x0, maxWidth: 220 });
		limits.damage.anchor.set(0, 0);
		limits.damage.position.set(0, limits.budget.y + limits.budget.height + 20);
		limits.addChild(limits.damage);

		limits.damage.value = new Text(`$${common.numberWithCommas(common.scenario.limits.damage)}`, { font: '30px', align: 'left', tint: 0x0, maxWidth: 180 });
		limits.damage.value.anchor.set(1, 0);
		limits.damage.value.position.set(320, 0);
		limits.damage.addChild(limits.damage.value);

		limits.fatalities = new Text('gui.overviewpanel.fatalities', { font: '30px', align: 'left', tint: 0x0, maxWidth: 220 });
		limits.fatalities.anchor.set(0, 0);
		limits.fatalities.position.set(0, limits.damage.y + limits.damage.height + 20);
		limits.addChild(limits.fatalities);

		limits.fatalities.value = new Text(`${common.numberWithCommas(common.scenario.limits.deaths)}`, { font: '30px', align: 'left', tint: 0x0, maxWidth: 180 });
		limits.fatalities.value.anchor.set(1, 0);
		limits.fatalities.value.position.set(320, 0);
		limits.fatalities.addChild(limits.fatalities.value);

		limits.fatalities.icon = new PIXI.Sprite(P3.assets.texture('icon_person'));
		limits.fatalities.icon.tint = 0x0;
		limits.fatalities.icon.anchor.set(0.5);
		limits.fatalities.icon.position.set(limits.fatalities.value.x - limits.fatalities.value.width - 10, limits.fatalities.value.height * 0.5);
		limits.fatalities.addChild(limits.fatalities.icon);

		const frame = new PIXI.Graphics();

		frame.lineStyle(2, 0xcbcbcb, 1);
		frame.drawRect(limits.x - 20, limits.y - 20, limits.width + 40, limits.height + 40);
		frame.endFill();
		section.addChild(frame);

		this._timerContainer = new PIXI.Container();
		section.addChild(this._timerContainer);

		const clockIcon = new PIXI.Sprite(P3.assets.texture('icon_clock'));

		clockIcon.anchor.set(0.5);
		clockIcon.position.set(0, 0);
		this._timerContainer.addChild(clockIcon);

		this.timeStart = new Text('gui.overviewpanel.timer.approx', { font: '30px', align: 'left', tint: 0x0 });
		this.timeStart.anchor.set(0, 0.5);
		this.timeStart.position.set(20, clockIcon.y);
		this._timerContainer.addChild(this.timeStart);

		this.timerClock = new Text('0:00', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFF0000 });
		this.timerClock.anchor.set(0, 0.5);
		this.timerClock.position.set(this.timeStart.x + this.timeStart.width, this.timeStart.y);
		this._timerContainer.addChild(this.timerClock);

		this.timerEnd = new Text('gui.overviewpanel.timer.until', { font: '30px', align: 'left', tint: 0x0 });
		this.timerEnd.anchor.set(0, 0.5);
		this.timerEnd.position.set(this.timerClock.x + this.timerClock.width, this.timerClock.y);
		this._timerContainer.addChild(this.timerEnd);

		this._timerContainer.position.set((-50 + this._panel.body.width * 0.5) - this._timerContainer.width * 0.5, mission.y + mission.height + 40);

		return section;
	}

	_createObjective(type) {
		const offset = 10;
		const goal = new PIXI.Container();

		goal.title = new Text(`gui.overviewpanel.goals.${type}.title`, { font: '30px', align: 'left', tint: 0x0, maxWidth: 200 });
		goal.title.anchor.set(0, 0);
		goal.title.position.set(0, offset);
		goal.addChild(goal.title);

		const target = type === 'hotel' ? common.scenario.targets.mission : common.scenario.targets[type];
		const value = i18next.t(`gui.overviewpanel.goals.${type}.value`, { ...this._strings, NUMBER: target });

		goal.value = new Text(value.toString(), { font: '30px', align: 'left', tint: 0x0, maxWidth: 200 });
		goal.value.anchor.set(1, 0);
		goal.value.position.set(400, offset);
		goal.addChild(goal.value);

		let largest = null;

		if (goal.title.height > goal.value.height) {
			goal.value.position.set(400, goal.title.y + ((goal.title.height * 0.5) - (goal.value.height * 0.5)));
			largest = goal.title;
		} else {
			goal.title.position.set(0, goal.value.y + ((goal.value.height * 0.5) - (goal.title.height * 0.5)));
			largest = goal.value;
		}

		goal.icon = new PIXI.Sprite(P3.assets.texture(`icon_objective_${type}`));
		goal.icon.anchor.set(0, 0.5);
		goal.icon.position.set(420, largest.y + largest.height * 0.5);
		goal.addChild(goal.icon);

		if (common.selectedLevel === 'wildfire' && type === 'hotel') {
			goal.value.text = i18next.t('gui.overviewpanel.goals.hotel.value_wf');
		} else if (common.selectedLevel === 'earthquake' && type === 'hotel') {
			goal.value.text = i18next.t('gui.overviewpanel.goals.hotel.value_eq', { NUMBER: common.scenario.targets.mission });
		} else if (common.selectedLevel === 'hurricane' && type === 'hotel') {
			goal.value.text = i18next.t('gui.overviewpanel.goals.hotel.value_hr');
		} else if (common.selectedLevel === 'flood' && type === 'hotel') {
			goal.value.text = i18next.t('gui.overviewpanel.goals.hotel.value_fd');
		}

		goal.highlight = new PIXI.Graphics();
		goal.highlight.beginFill(0xddfdd2, 1);
		goal.highlight.drawRect(-10, 0, 500, goal.height + 10);
		goal.highlight.endFill();
		goal.highlight.visible = true;
		goal.addChildAt(goal.highlight, 0);

		const divider = new PIXI.Sprite(P3.assets.texture('divider'));

		divider.anchor.set(0, 0.5);
		divider.width = 500;
		divider.height = 1;
		divider.position.set(-10, goal.height - 1);
		goal.addChild(divider);

		return goal;
	}

	displaySeconds(time) {
		// const h = P3.utils.padNum(Math.floor((time / 60) / 60), 2);
		const m = P3.utils.padNum(Math.floor(time / 60), 2);
		const s = P3.utils.padNum(Math.floor(time % 60), 2);

		return `${m}:${s}`;
	}

	update() {
		if (common.scenario.ended || common.scenario.finished) {
			this.timerClock.text = '  0:00  ';
		} else {
			this.timerClock.text = `  ${this.displaySeconds(common.scenario._time - common.scenario._elapsedTime)}  `;
		}
		this.timerEnd.position.set(this.timerClock.x + this.timerClock.width, this.timerClock.y);
	}

	resize() {
		super.resize();

		if (this._fade) {
			this._fade.width = P3.View.width;
			this._fade.height = P3.View.height;
			this._fade.position.set(P3.View.width * -0.5, this._fade.height * -0.5);
		}
	}

	toggleObjective(which, state) {
		if (state) {
			this._objectives[which].highlight.visible = true;
		} else {
			this._objectives[which].highlight.visible = false;
		}
	}

	appear() {
		this.parent.addChild(this); // set to top

		this.checkObjectives();

		this.scale.set(0.8);
		this._fade.visible = true;
		this.visible = true;

		new TWEEN.Tween(this.scale, this.tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();
	}

	destroy() {
		common.languageManager.signals.changed.detach(this._languageChangeSignal, this);

		super.destroy();
	}
}
